import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ClipboardList } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EQUIP = ["Dry Van", "Reefer", "Flatbed", "Step-Deck", "Power Only", "Box Truck"];

export default function BookLoad() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preselected = sp.get("service") || "";
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [done, setDone] = useState(null);
  const [f, setF] = useState({
    origin: "", destination: "",
    equipment_type: preselected.toLowerCase().includes("reefer") || preselected.toLowerCase().includes("temperature") ? "Reefer"
      : preselected.toLowerCase().includes("flatbed") ? "Flatbed" : "Dry Van",
    pickup_date: "", weight_lbs: "",
  });

  useEffect(() => {
    if (!loading && !user) {
      nav(`/login?role=customer&next=${encodeURIComponent("/book" + (preselected ? `?service=${preselected}` : ""))}`, { replace: true });
    }
  }, [loading, user, nav, preselected]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return null;
  if (user.role !== "customer") {
    return <div className="max-w-xl mx-auto py-20 px-4 text-center text-slate-600">Booking is only available for customer accounts. Please sign in with a customer profile.</div>;
  }

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoadingSubmit(true);
    try {
      const { data } = await api.post("/shipments", { ...f, weight_lbs: parseFloat(f.weight_lbs) || 0 });
      setDone(data);
      toast.success(`Load booked: ${data.shipment_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Booking failed");
    } finally { setLoadingSubmit(false); }
  };

  if (done) {
    return (
      <div className="min-h-[70vh] bg-gray-50 py-20 px-4">
        <div className="max-w-lg mx-auto bg-white border border-gray-200 rounded-lg p-10 text-center" data-testid="book-success">
          <CheckCircle2 className="h-14 w-14 text-[#2E7BC7] mx-auto" />
          <h2 className="mt-6 font-display font-black text-3xl text-[#0B3D6E]">Booking received</h2>
          <p className="mt-3 text-slate-600">Reference number <span className="font-mono font-bold text-[#0B3D6E]">{done.shipment_id}</span></p>
          <div className="mt-6 rounded-md bg-blue-50 border border-blue-100 p-5 text-left text-sm text-slate-700">
            Our ops team will <span className="font-semibold">contact you shortly</span> to confirm the rate, pickup window, and driver details. For anything urgent, call <span className="font-semibold">778-861-7890</span>.
          </div>
          <div className="mt-6 flex gap-2 justify-center">
            <button onClick={() => nav("/portal/customer/shipments")} className="btn-primary-apex" data-testid="book-view-shipments">View shipments</button>
            <button onClick={() => { setDone(null); setF({ ...f, origin: "", destination: "", pickup_date: "", weight_lbs: "" }); }} className="btn-secondary-apex" data-testid="book-another">Book another</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-16 px-4">
      <div className="max-w-xl mx-auto">
        <div className="font-label text-xs text-[#2E7BC7]">Book Load {preselected ? `— ${preselected}` : ""}</div>
        <h1 className="mt-2 font-display font-black text-3xl md:text-4xl text-[#0B3D6E]">Send us your load details.</h1>
        <p className="mt-3 text-slate-600">Submit your booking request — our ops team will confirm the rate, pickup window, and driver details by phone or email.</p>
        <form onSubmit={submit} className="mt-8 bg-white border border-gray-200 rounded-lg p-8 space-y-5" data-testid="book-form">
          <Field label="Origin (City, State)" v={f.origin} on={upd("origin")} required test="book-origin" />
          <Field label="Destination (City, State)" v={f.destination} on={upd("destination")} required test="book-destination" />
          <div>
            <label className="text-sm font-semibold text-slate-700 mb-1 block">Equipment</label>
            <Select value={f.equipment_type} onValueChange={(v) => setF({ ...f, equipment_type: v })}>
              <SelectTrigger data-testid="book-equipment"><SelectValue /></SelectTrigger>
              <SelectContent>{EQUIP.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Pickup date" type="date" v={f.pickup_date} on={upd("pickup_date")} required test="book-pickup-date" />
          <Field label="Weight (lbs)" type="number" v={f.weight_lbs} on={upd("weight_lbs")} required test="book-weight" />
          <button disabled={loadingSubmit} className="btn-primary-apex w-full inline-flex items-center justify-center gap-2" data-testid="book-submit-btn">
            <ClipboardList className="h-4 w-4" /> {loadingSubmit ? "Booking…" : "Book Load"}
          </button>
        </form>
      </div>
    </div>
  );
}
function Field({ label, v, on, type = "text", required, test }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-700 mb-1 block">{label}</label>
      <input type={type} required={required} value={v} onChange={on}
        className="w-full bg-white border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-[#2E7BC7] focus:border-transparent outline-none"
        data-testid={test} />
    </div>
  );
}
